import * as React from "react";
import { v4 as uuidv4 } from "uuid";

interface SingleNavMenuLinkId {
  id: string;
}

export interface SingleNavMenuLinkCategory extends SingleNavMenuLinkId {
  isCategory: true;
  label: string;
}

export interface SingleNavMenuLinkCategoryEnd extends SingleNavMenuLinkId {
  isCategoryEnd: true;
}
export interface SingleNavMenuLinkDivider extends SingleNavMenuLinkId {
  isDivider: true;
}
export interface SingleNavMenuLinkSpace extends SingleNavMenuLinkId {
  isSpace: true;
}
export interface SingleNavMenuLinkExternal extends SingleNavMenuLinkId {
  isExternal: true;
  label: string;
  type: ToLinkExternalType;
  clusterAlias?: string;
  clusterName?: string;
}
export interface SingleNavMenuLinkPage extends SingleNavMenuLinkId {
  isPage: true;
  label: string;
  page: string;
  names?: string[];
  showDirty?: boolean;
  subLevel?: 1 | 2;
  renderIcon?: () => React.ReactNode;
}
export interface SingleNavMenuLinkSection extends SingleNavMenuLinkId {
  isSection: true;
  label: string;
  page: string;
  section: string;
  sub?: number;
  names?: string[];
}
export type SingleNavMenuLinkButtonType = "primary" | "default";
export interface SingleNavMenuLinkButton extends SingleNavMenuLinkId {
  isButton: true;
  label: string;
  type: SingleNavMenuLinkButtonType;
  className?: string;
  htmlType?: "submit" | "button";
  danger?: boolean;
  disabled?: boolean;
  loading?: boolean;
  onClick?: any;
  onMouseDown?: any;
}
export interface SingleNavMenuLinkButtonWithOptions extends SingleNavMenuLinkId {
  isButtonWithOptions: true;
  label: string;
  type: SingleNavMenuLinkButtonType;
  className?: string;
  htmlType?: "submit" | "button";
  danger?: boolean;
  disabled?: boolean;
  loading?: boolean;
  onClick: any;
  options: SingleNavMenuLinkButton[];
}
export interface SingleNavMenuLinkAction extends SingleNavMenuLinkId {
  isAction: true;
  label: string;
  onClick: () => void;
  disabled?: boolean;
  loading?: boolean;
}

export type SingleNavMenuLink =  //
  | SingleNavMenuLinkCategory
  | SingleNavMenuLinkCategoryEnd
  | SingleNavMenuLinkDivider
  | SingleNavMenuLinkSpace
  | SingleNavMenuLinkExternal
  | SingleNavMenuLinkPage
  | SingleNavMenuLinkSection
  | SingleNavMenuLinkButton
  | SingleNavMenuLinkButtonWithOptions
  | SingleNavMenuLinkAction;

export function toLinkCategory(label: string): SingleNavMenuLinkCategory {
  return {
    id: uuidv4(),
    isCategory: true,
    label: label,
  };
}
export function toLinkCategoryEnd(): SingleNavMenuLinkCategoryEnd {
  return {
    id: uuidv4(),
    isCategoryEnd: true,
  };
}
export function toLinkDivider(): SingleNavMenuLinkDivider {
  return {
    id: uuidv4(),
    isDivider: true,
  };
}
export function toLinkSpace(): SingleNavMenuLinkSpace {
  return {
    id: uuidv4(),
    isSpace: true,
  };
}
type ToLinkExternalType =
  | "traces-org" //
  | "metrics-org"
  | "traces-gvc"
  | "metrics-gvc"
  | "traces-workload"
  | "metrics-workload"
  | "metrics-mk8s";
export function toLinkExternal(
  label: string,
  type: ToLinkExternalType,
  clusterAlias?: string,
  clusterName?: string,
): SingleNavMenuLinkExternal {
  return {
    id: uuidv4(),
    isExternal: true,
    label: label,
    type: type,
    clusterAlias,
    clusterName,
  };
}
export function toLinkPage(
  label: string,
  page: string,
  opts?: { subLevel?: 1 | 2; names?: string[]; showDirty?: boolean; renderIcon?: () => React.ReactNode },
): SingleNavMenuLinkPage {
  return {
    id: uuidv4(),
    isPage: true,
    label: label,
    page: page,
    names: opts?.names,
    showDirty: opts?.showDirty,
    subLevel: opts?.subLevel,
    renderIcon: opts?.renderIcon,
  };
}
export function toLinkSection(
  label: string,
  page: string,
  section: string,
  opts?: { sub?: number; names?: string[] },
): SingleNavMenuLinkSection {
  return {
    id: uuidv4(),
    isSection: true,
    label: label,
    page: page,
    section: section,
    sub: opts?.sub,
    names: opts?.names,
  };
}
export function forPage(
  page: string,
): {
  toLinkSection: (
    label: string,
    section: string,
    opts?: { sub?: number; names?: string[] },
  ) => SingleNavMenuLinkSection;
} {
  return {
    toLinkSection: (label: string, section: string, opts?: { sub?: number; names?: string[] }) =>
      toLinkSection(label, page, section, opts),
  };
}
export function toLinkButton(
  label: string,
  {
    type,
    danger,
    disabled,
    loading,
    onClick,
    onMouseDown,
    className,
    htmlType = "button",
  }: {
    type: SingleNavMenuLinkButtonType;
    danger?: boolean;
    disabled?: boolean;
    loading?: boolean;
    onClick?: any;
    onMouseDown?: any;
    className?: string;
    htmlType?: "submit" | "button";
  },
): SingleNavMenuLinkButton {
  return {
    id: uuidv4(),
    isButton: true,
    label: label,
    type: type,
    danger: danger,
    disabled: disabled,
    onClick: onClick,
    onMouseDown: onMouseDown,
    loading: loading,
    className: className,
    htmlType: htmlType,
  };
}
export function toLinkButtonWithOptions(
  label: string,
  self: Omit<SingleNavMenuLinkButton, "isButton" | "label" | "id">,
  options: Omit<SingleNavMenuLinkButton, "isButton" | "id">[] = [],
): SingleNavMenuLinkButtonWithOptions {
  return {
    id: uuidv4(),
    isButtonWithOptions: true,
    label: label,
    danger: self.danger,
    onClick: self.onClick,
    type: self.type,
    disabled: self.disabled,
    className: self.className,
    htmlType: self.htmlType,
    loading: self.loading,
    options: options.map((opt) => ({ id: uuidv4(), isButton: true, ...opt })),
  };
}
export function toLinkAction(
  label: string,
  opts: { onClick: () => void; disabled?: boolean; loading?: boolean },
): SingleNavMenuLinkAction {
  return {
    id: uuidv4(),
    isAction: true,
    label: label,
    onClick: opts.onClick,
    disabled: opts.disabled,
    loading: opts.loading,
  };
}
